import { Box, Stack, TextField } from "@mui/material";
import { GridToolbarContainer, GridToolbarProps, useGridRootProps } from "@mui/x-data-grid";
import { useEffect, useMemo, useRef, useState } from "react";

import { useCustomDataGridContext } from "@/common/contexts/customDataGrid";
import { TypeHelper } from "@/common/helpers/type";
import {
  DataGridCustomSortProps,
  DataGridFilterProps,
  DataGridQuickFilterProps,
  DataGridRefetchProps,
} from "@/common/ts/dataGrid";
import { AppTheme } from "@/theme";
import { ThemeHelper } from "@/theme/helper";

import AppBadge from "../Badge/AppBadge";
import { DataListTabsProps } from "../DataList/DataList";
import { DataTabularTabs } from "../DataTabular/DataTabularTabs";
import { ToolbarIconButton } from "../DataTabular/ToolbarIconButton";
import FilterPopover, { FilterPopoverHandle } from "../Filters/FilterPopover";
import AppIcon from "../Icons/AppIcon";
import AppPopover from "../Popover/AppPopover";
import AppPopoverContent from "../Popover/AppPopoverContent";
import SortPopover from "../Sort/SortPopover";
import AppTooltip from "../Tooltip/AppTooltip";
import CustomGridColumnsManagement from "./CustomGridColumnsManagement";

declare module "@mui/x-data-grid" {
  interface GridToolbarProps {
    // showQuickFilter?: boolean;
    // quickFilterProps?: GridToolbarQuickFilterProps;
    tabs?: DataListTabsProps;
    sort?: DataGridCustomSortProps;
    quickFilter?: DataGridQuickFilterProps;
    filters?: DataGridFilterProps;
    refetch?: DataGridRefetchProps;
  }
}

interface Props extends GridToolbarProps {
  // tabs?: DataListTabsProps;
}

export default function CustomDataGridToolbar({
  tabs,
  sort,
  quickFilter,
  filters,
  refetch,
}: Props) {
  // const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  // const preferencePanelState = useGridSelector(apiRef, gridPreferencePanelStateSelector);
  const customDataGridContext = useCustomDataGridContext();
  const filterPopoverRef = useRef<FilterPopoverHandle>(null);

  const [isSearchOpened, setIsSearchOpened] = useState(false);
  const [search, setSearch] = useState<string | undefined>(quickFilter?.initialSearch);
  const [isRefetching, setIsRefetching] = useState(false);

  const isTabsEnabled = useMemo(() => !!tabs && (tabs.default || tabs.custom), [tabs]);
  const isColumnSelectorEnabled = useMemo(
    () => !rootProps.disableColumnSelector,
    [rootProps.disableColumnSelector],
  );
  const isSortEnabled = useMemo(() => !!sort, [sort]);
  const isQuickFilterEnabled = useMemo(() => !!quickFilter, [quickFilter]);
  const isFiltersEnabled = useMemo(() => !!filters, [filters]);
  const isRefetchEnabled = useMemo(() => !!refetch, [refetch]);
  const enabledList = [
    isTabsEnabled,
    isColumnSelectorEnabled,
    isSortEnabled,
    isQuickFilterEnabled,
    isFiltersEnabled,
    isRefetchEnabled,
  ];
  const isAnyEnabled = useMemo(() => enabledList.some((x) => x === true), [...enabledList]);

  // DataGrid column filter integration
  useEffect(() => {
    const sub = customDataGridContext.on2("addFieldFilterRequested", (params) => {
      filterPopoverRef.current?.addFieldFilterAndOpen(params);
    });
    return () => {
      sub.off();
    };
  }, []);

  // refetch integration
  useEffect(() => {
    const sub1 = refetch?.eventEmitter.on2("refetchStarted", () => {
      setIsRefetching(true);
    });
    const sub2 = refetch?.eventEmitter.on2("refetchEnded", () => {
      setIsRefetching(false);
    });
    return () => {
      sub1?.off();
      sub2?.off();
    };
  }, [refetch]);

  if (!isAnyEnabled) {
    return null;
  }

  return (
    <GridToolbarContainer
      data-testid='custom-data-grid-toolbar'
      sx={{
        p: 0,
        border: "none",
        borderBottom: (th) => `1px solid ${th.palette.divider}`,
        // ensure border color is synced with tabs color
        ...(isTabsEnabled
          ? { borderBottom: (th) => `1px solid ${ThemeHelper.getTabsBorderColor(th as AppTheme)}` }
          : undefined),
      }}
    >
      <Box
        data-testid='custom-data-grid-toolbar-content'
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: { xxs: "1fr", lg: "2fr 1fr" },
          columnGap: 1,
          rowGap: 0,
        }}
      >
        {/* Tabs */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          {isTabsEnabled && tabs && (
            <>
              {tabs.default && (
                <DataTabularTabs
                  {...tabs.default}
                  tabsProps={{
                    variant: "scrollable",
                    allowScrollButtonsMobile: true,
                    isFullHeight: true,
                    ...tabs.default.tabsProps,
                  }}
                  tabPropsList={tabs.default.tabPropsList}
                />
              )}

              {tabs.custom &&
                tabs.custom({
                  tabsProps: {
                    variant: "scrollable",
                    allowScrollButtonsMobile: true,
                    isFullHeight: true,
                  },
                  tabProps: {},
                })}
            </>
          )}
        </Box>

        {/* Actions */}
        <Stack
          direction='row'
          gap={0.25}
          sx={{ justifyContent: "flex-end", alignItems: "center", pr: 1, py: 1 }}
        >
          {/* <GridToolbar showQuickFilter /> */}

          {/* <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarFilterButton />
      <GridToolbarQuickFilter /> */}

          {/* <GridColumnsManagement /> */}

          {/* <GridToolbarFilterButton /> */}

          {/* Quick filter */}
          {isQuickFilterEnabled && quickFilter && !isSearchOpened && (
            <AppTooltip title='Quick search' isDefaultEnterDelays>
              <AppBadge
                variant='dot'
                color='secondary'
                overlap='circular'
                invisible={TypeHelper.isEmpty(search)}
              >
                <ToolbarIconButton onClick={() => setIsSearchOpened(true)}>
                  <AppIcon of='search' />
                </ToolbarIconButton>
              </AppBadge>
            </AppTooltip>
          )}
          {isQuickFilterEnabled && quickFilter && isSearchOpened && (
            <TextField
              sx={{ flex: 1, "& .MuiInputBase-root": { pr: 0 }, minWidth: 0 }}
              label='Search'
              size='small'
              value={search || ""}
              onChange={(e) => {
                setSearch(e.target.value);
                quickFilter.onChange(e.target.value || undefined);
              }}
              autoFocus
              InputProps={{
                endAdornment: (
                  <ToolbarIconButton
                    onClick={() => {
                      setIsSearchOpened(false);
                      setSearch(undefined);
                      quickFilter.onChange(undefined);
                    }}
                  >
                    <AppIcon of='close' />
                  </ToolbarIconButton>
                ),
              }}
            />
          )}

          {/* Opens DataGrid's columns panel via DataGrid API */}
          {/* <AppTooltip title='Columns' isDefaultEnterDelays>
        <AppIconButton
          onClick={() => {
            if (
              preferencePanelState.open &&
              preferencePanelState.openedPanelValue === GridPreferencePanelsValue.columns
            ) {
              apiRef.current.hidePreferences();
            } else {
              apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
            }
          }}
        >
          <AppIcon of='columns' />
        </AppIconButton>
      </AppTooltip> */}

          {/* Filters */}
          {isFiltersEnabled && filters && filters.filterSpec && (
            <FilterPopover
              ref={filterPopoverRef}
              clickBehavior={{}}
              trigger={
                <AppTooltip title='Filters' isDefaultEnterDelays>
                  <AppBadge
                    color='secondary'
                    overlap='circular'
                    badgeContent={filters.filterDefinition?.items?.length}
                  >
                    <ToolbarIconButton>
                      <AppIcon of='filters' />
                    </ToolbarIconButton>
                  </AppBadge>
                </AppTooltip>
              }
              filterDefinitionInputProps={{
                filterSpec: filters.filterSpec,
                filterDefinition: filters.filterDefinition,
                onChange: filters.onChange,
              }}
            />
          )}

          {/* Sort */}
          {isSortEnabled && sort && sort.sortSpec && (
            <SortPopover
              clickBehavior={{}}
              trigger={
                <AppTooltip title='Sort' isDefaultEnterDelays>
                  <AppBadge
                    color='secondary'
                    overlap='circular'
                    badgeContent={sort.sortDefinition?.items?.length}
                  >
                    <ToolbarIconButton>
                      <AppIcon of='sorting' />
                    </ToolbarIconButton>
                  </AppBadge>
                </AppTooltip>
              }
              sortDefinitionInputProps={{
                sortSpec: sort.sortSpec,
                sortDefinition: sort.sortDefinition,
                maxSortItems: sort.maxSortItems,
                onChange: sort.onChange,
              }}
            />
          )}

          {/* Manually render columns panel. */}
          {isColumnSelectorEnabled && (
            <AppPopover
              clickBehavior={{}}
              trigger={
                <AppTooltip title='Columns' isDefaultEnterDelays>
                  <ToolbarIconButton>
                    <AppIcon of='columns' />
                  </ToolbarIconButton>
                </AppTooltip>
              }
            >
              <AppPopoverContent isUseDefaultStyles={false}>
                <CustomGridColumnsManagement {...rootProps.slotProps?.columnsManagement} />
              </AppPopoverContent>
            </AppPopover>
          )}

          {/* Refetch */}
          {isRefetchEnabled && refetch && (
            <AppTooltip title='Refresh' isDefaultEnterDelays>
              <ToolbarIconButton
                disabled={isRefetching}
                onClick={(e) => {
                  refetch.triggerRefetch();
                }}
              >
                <AppIcon of='refresh' />
              </ToolbarIconButton>
            </AppTooltip>
          )}
        </Stack>
      </Box>
    </GridToolbarContainer>
  );
}
